import { ErrorCircle, HelpCircle } from '@ori-ui/icons';
import type { SelectChangeEvent } from '@ori-ui/mui';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Tooltip,
} from '@ori-ui/mui';
import type { GlobalSharedMetadataValidationStringValidationDto } from '@ori/global-api-client-react';
import type { StaticCountriesCountryDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import camelCase from 'lodash.camelcase';
import split from 'lodash.split';
import { useState, useEffect } from 'react';
import type { FC, ReactElement } from 'react';
import ReactCountryFlag from 'react-country-flag';
import type { ControllerFieldState } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useFormError } from '../../../contexts';
import { TranslationKey } from '../../../models';
import { usePhoneFormats, useTenantsCountry } from '../../../models/api';
import { getErrorLocalizationByCode, renderValidationMessage } from '../../../utils';

interface ISelectOption {
  label: ReactElement | string;
  value: string;
  countryCode?: string;
}

interface IPhoneFieldByConfigProps {
  name: string;
  label?: ReactElement | string;
  tooltipText: ReactElement | string;
  isEditable?: boolean;
  isAvailable?: boolean;
  isAddressMatchRequired?: boolean;
  displayOrder?: number;
  validation?: GlobalSharedMetadataValidationStringValidationDto;
  fieldState?: ControllerFieldState;
  initialValue?: string;
  customPlaceholder?: string;
  customPlaceholderWithPrefix?: string;
  tooltipWithPrefix: ReactElement | string;
  labelWithPrefix?: ReactElement | string;
  inputType?: React.InputHTMLAttributes<unknown>['type'];
  enableLowercaseLetters?: boolean;
}

const StyledHelpCircle = styled(HelpCircle)(({ theme }) => ({
  fontSize: '30px',
  color: theme.palette.grey[300],
}));

const FlagWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(3),
}));

const StyledLink = styled('a')(({ theme }) => ({
  color: theme.palette.error.main,
  textDecoration: 'underline',
  '&:hover': {
    color: theme.palette.error.main,
    textDecoration: 'underline',
  },
}));

const getCountryByCountryCode = (countries?: StaticCountriesCountryDto[], countryCode?: string) =>
  countries?.find((country) => country.countryCode === countryCode)?.name;

export const PhoneFieldByConfig: FC<IPhoneFieldByConfigProps> = (props) => {
  const {
    name,
    label,
    tooltipText,
    isEditable,
    isAvailable,
    displayOrder,
    validation,
    initialValue,
    isAddressMatchRequired,
    inputType,
    customPlaceholder,
    enableLowercaseLetters,
    tooltipWithPrefix,
    customPlaceholderWithPrefix,
    labelWithPrefix,
  } = props;

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const { customerLoginUrl } = useConfiguration();

  const { setFocus, setValue, setError, watch, clearErrors } = useFormContext();
  const [phonePrefix, setPhonePrefix] = useState<string>();
  const [isDisabledPrefix, setIsDisabledPrefix] = useState(false);

  const [phoneWithoutPrefix, setPhoneWithoutPrefix] = useState<string>();

  const { data: phoneFormats } = usePhoneFormats();
  const { data: countries } = useTenantsCountry();

  const availableOptions = phoneFormats?.allowedFormats.map((opt) => ({
    countryCode: opt.countryCode === 'UK' ? 'GB' : opt.countryCode,
    label: opt.internationalPrefix,
    value: opt.internationalPrefix,
  })) as ISelectOption[] | undefined;

  const isAnyPhoneFormats = Boolean(availableOptions?.length && availableOptions.length > 0);
  const onlyOnePrefix = availableOptions?.length === 1;

  const { getTestIdProps, getTestId } = useTestId();

  const submitError = customErrors?.find((error) => error.fieldName?.toLowerCase() === name.toLowerCase());

  const watchValue = watch(name) as string;

  const formatError = split(submitError?.fieldName, '.').map((item) => camelCase(item));
  const joinFormatError = formatError.join('.');

  useEffect(() => {
    if (submitError) {
      setFocus(joinFormatError);
    }
  }, [customErrors, joinFormatError, setFocus, submitError]);

  const renderSelectOption = (item: ISelectOption, index: number): ReactElement => (
    <MenuItem
      key={index}
      value={item.value}
      {...getTestIdProps('select-field-option')}
    >
      {item.countryCode ? (
        <ReactCountryFlag
          svg
          countryCode={item.countryCode}
          style={{
            width: '18px',
            height: '18px',
            objectFit: 'cover',
            borderRadius: '50%',
            marginRight: '8px',
          }}
        />
      ) : null}
      {getCountryByCountryCode(countries, item.countryCode)} ({item.label})
    </MenuItem>
  );

  const errorMessage = getErrorLocalizationByCode(submitError?.errorCode, name);

  const regexPattern = validation?.regularExpression ? new RegExp(validation.regularExpression) : undefined;

  const readOnly = isAddressMatchRequired ?? false;
  const fieldPlaceholder = isAnyPhoneFormats ? customPlaceholderWithPrefix : customPlaceholder;

  const initialPrefix = availableOptions?.find((aopt) => initialValue?.includes(aopt.value));

  useEffect(() => {
    if (!phonePrefix && initialPrefix) {
      setPhonePrefix(initialPrefix.value);
    } else if (availableOptions && !phonePrefix) {
      setPhonePrefix(availableOptions[0]?.value);
    } else {
      setPhonePrefix(phonePrefix);
    }
  }, [initialPrefix, phonePrefix, availableOptions]);

  useEffect(() => {
    if (watchValue && phonePrefix) {
      setPhoneWithoutPrefix(watchValue.replace(phonePrefix, ''));
    }
  }, [watchValue, phonePrefix]);

  useEffect(() => {
    if (availableOptions && onlyOnePrefix) {
      setIsDisabledPrefix(true);
    }
    if (availableOptions && !initialPrefix && !phonePrefix) {
      setPhonePrefix(availableOptions[0]?.value);
    }
  }, [availableOptions, onlyOnePrefix, initialPrefix, phonePrefix]);

  const capitalize = enableLowercaseLetters ? 'off' : 'on';
  const readOnlyStyleBox = {
    display: 'flex',
    height: '0',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const renderHelpTooltip = () => (
    <Tooltip
      title={isAnyPhoneFormats ? tooltipWithPrefix : tooltipText}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
    >
      <StyledHelpCircle />
    </Tooltip>
  );

  const renderErrorTooltip = () => (
    <Tooltip
      title={isAnyPhoneFormats ? tooltipWithPrefix : tooltipText}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
    >
      <ErrorCircle color="error" />
    </Tooltip>
  );

  const handleChangeAllowOnlyNumber = (available: boolean, value: string) => {
    let correctValue = value;
    if (available && correctValue.startsWith('0')) {
      correctValue = correctValue.slice(1).replaceAll(/\D/g, '');
    }

    if (available) {
      correctValue = correctValue.replaceAll(/\D/g, '');
    }

    const currentValue = isAnyPhoneFormats
      ? // eslint-disable-next-line unicorn/prefer-spread -- we need this type concat
        correctValue && phonePrefix && phonePrefix.concat(correctValue)
      : correctValue;

    const isValid = currentValue ? regexPattern?.test(currentValue) : false;
    if (isValid) {
      clearErrors(name);
    } else {
      setError(name, { type: 'pattern' });
    }

    return currentValue?.trim();
  };

  const handlePrefixChange = (event: SelectChangeEvent<typeof phonePrefix>) => {
    const {
      target: { value },
    } = event;
    setPhonePrefix(value);
    // eslint-disable-next-line unicorn/prefer-spread -- we need this type concat
    const currentValue = phoneWithoutPrefix && value && value.concat(phoneWithoutPrefix);
    if (watchValue) {
      setValue(name, currentValue?.trim(), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;

  const onlyOneOption = Boolean(availableOptions?.length && availableOptions.length === 1);

  const renderField = () => (
    <Grid
      item
      xs={12}
      sm={12}
      style={{ order: displayOrder }}
    >
      <Box
        margin={readOnly ? '0' : '0 auto 25px'}
        maxWidth={500}
        style={readOnly ? readOnlyStyleBox : undefined}
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        {isAnyPhoneFormats ? (
          <Box
            width="auto"
            marginRight={4}
          >
            <FormControl
              key={phonePrefix}
              fullWidth
              required={validation?.isMandatory}
            >
              <InputLabel id={`${name}-prefix-label`}>{labelWithPrefix}</InputLabel>
              <Select
                displayEmpty
                labelId={`${name}-prefix-label`}
                label={labelWithPrefix}
                variant="outlined"
                required={validation?.isMandatory}
                disabled={!isEditable || isDisabledPrefix}
                inputProps={{
                  tabIndex: correctTabIndex,
                }}
                MenuProps={{
                  sx: {
                    '& .MuiPaper-root': {
                      width: '500px',
                      border: '1px solid #CCCCCC',
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                renderValue={(selected?: string) => {
                  if (!selected?.length) {
                    return undefined;
                  }

                  const foundCode = availableOptions?.find((opt) => opt.value === selected);
                  if (foundCode?.countryCode) {
                    return (
                      <FlagWrapper>
                        <ReactCountryFlag
                          svg
                          countryCode={foundCode.countryCode}
                          style={{
                            width: '18px',
                            height: '18px',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        />
                        {selected}
                      </FlagWrapper>
                    );
                  }

                  return selected;
                }}
                sx={{
                  '.MuiSelect-icon': {
                    display: onlyOneOption ? 'none' : 'block',
                  },
                }}
                value={phonePrefix}
                defaultValue={phonePrefix}
                onChange={(e) => handlePrefixChange(e)}
              >
                {availableOptions?.map(renderSelectOption)}
              </Select>
            </FormControl>
          </Box>
        ) : null}
        <Box
          maxWidth={isAnyPhoneFormats ? 380 : 500}
          width="100%"
          display="flex"
          alignItems="end"
        >
          <Controller
            name={name}
            rules={{
              required: validation?.isMandatory,
              maxLength: validation?.maxLength,
              minLength: validation?.minLength,
              pattern: regexPattern,
            }}
            defaultValue={initialValue}
            render={({ field: { ref, onChange, ...field }, fieldState }) => (
              <FormControl fullWidth>
                {readOnly ? null : (
                  <TextField
                    fullWidth
                    label={isAnyPhoneFormats ? null : label}
                    type={inputType ?? 'text'}
                    placeholder={fieldPlaceholder}
                    variant="outlined"
                    error={!!fieldState.error || Boolean(submitError)}
                    helperText={
                      fieldState.error ? (
                        <div {...getTestIdProps(`${name}-phone-field-error-message`)}>
                          {renderValidationMessage(fieldState.error)}
                        </div>
                      ) : null
                    }
                    disabled={!isEditable}
                    required={validation?.isMandatory}
                    inputProps={{
                      maxLength: validation?.maxLength,
                      minLength: validation?.minLength ?? undefined,
                      inputMode: 'tel',
                      'data-testid': getTestId(`${name}-phone-field`),
                      autoCapitalize: capitalize,
                      tabIndex: correctTabIndex,
                      sx: {
                        '&::placeholder': {
                          textTransform: 'initial',
                        },
                      },
                    }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
                    InputProps={{
                      endAdornment: fieldState.error ? renderErrorTooltip() : renderHelpTooltip(),
                      autoComplete: 'off',
                    }}
                    {...field}
                    value={(field.value as string | undefined)?.replace(phonePrefix as string, '')}
                    inputRef={ref}
                    onChange={(e) => onChange(handleChangeAllowOnlyNumber(isAnyPhoneFormats, e.target.value))}
                  />
                )}
                {submitError ? (
                  <FormHelperText error>
                    <Localization
                      {...errorMessage}
                      values={{
                        loginLink: (
                          <StyledLink
                            href={customerLoginUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Localization
                              id={TranslationKey.loginLinkLabel}
                              defaultMessage={defaultTranslations.loginLinkLabel}
                            />
                          </StyledLink>
                        ),
                      }}
                    />
                  </FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
        </Box>
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
